import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Grid,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  ListItem,
  Divider,
  Stack,
  Link,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Assignment,
  WbSunny,
  Map,
  Warning,
  Security,
  Description,
  ArrowBack,
  Info,
  Circle
} from '@mui/icons-material';

const ResourceLink = ({ icon, primary, secondary, href, external }) => (
  <ListItemButton 
    component={Link}
    href={href}
    target={external ? "_blank" : "_self"}
    rel={external ? "noopener noreferrer" : ""}
    sx={{ 
      py: 1.5,
      px: 2,
      color: '#2e2e2e',
      textDecoration: 'none',
      '&:hover': {
        backgroundColor: 'rgba(53, 152, 183, 0.08)',
      }
    }}
  >
    <ListItemIcon sx={{ color: '#3598b7', minWidth: 40 }}>
      {icon}
    </ListItemIcon>
    <ListItemText 
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{
        sx: { color: 'inherit', fontWeight: 500 }
      }}
      secondaryTypographyProps={{
        sx: { color: 'rgba(46, 46, 46, 0.7)' }
      }}
    />
  </ListItemButton>
);

const InfoItem = ({ text }) => (
  <ListItem sx={{ py: 1, px: 2 }}>
    <ListItemIcon sx={{ minWidth: 28 }}>
      <Circle sx={{ color: '#3598b7', fontSize: '0.8rem' }} />
    </ListItemIcon>
    <ListItemText 
      primary={text}
      primaryTypographyProps={{
        sx: { 
          color: '#2e2e2e',
          fontSize: '0.95rem'
        }
      }}
    />
  </ListItem>
);

const PlanMission = () => {
  const navigate = useNavigate();

  const requiredLinks = [
    {
      icon: <Assignment />,
      primary: "Mission Form Control (MFC)",
      secondary: "Gjennomfør MFC før flyging",
      href: "https://form.jotform.com/213212604878354",
      external: true
    },
    {
      icon: <Security />,
      primary: "Job Safety Analysis (JSA)",
      secondary: "Gjennomfør JSA",
      href: "https://form.jotform.com/213192990452357",
      external: true
    },
    {
      icon: <Description />,
      primary: "Dronelogbook",
      secondary: "Opprett mission og få godkjenning",
      href: "https://www.dronelogbook.com",
      external: true
    }
  ];

  const areaChecks = [
    {
      icon: <Map />,
      primary: "DJI FlySafe Kart",
      secondary: "Sjekk restriksjoner",
      href: "https://www.dji.com/no/flysafe/geo-map",
      external: true
    },
    {
      icon: <Warning />,
      primary: "NSM Forbudsområder",
      secondary: "Sjekk forbudsområder",
      href: "https://nsm.geodataonline.no/sensorapp/",
      external: true
    },
    {
      icon: <Map />,
      primary: "Safe to Fly",
      secondary: "Sjekk naturreservat og andre restriksjoner",
      href: "https://www.safetofly.no/",
      external: true
    }
  ];

  const checklistItems = {
    'Planlegging': [
      "Teknisk stand i Dronelogbook (INOP/Maintenance)",
      "PPR / NOTAM",
      "NINOX Drone status",
      "Vær / Solstorm (obs på bl.a ising)",
      "Restriksjoner: Naturreservat eller andre restriksjoner"
    ],
    'Før avreise': [
      "Gå gjennom oppdraget og målet for jobben",
      "Bekrefte eventuelle tillatelser for de aktuelle lokasjonene",
      "Sjekk været",
      "Sjekk NOTAM",
      "Sjekk batterinivåer",
      "Boot Inspire 2/3 og bekreft operativ tilstand"
    ]
  };

  return (
    <Box sx={{ 
      p: 3, 
      maxWidth: 1200, 
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#2e2e2e', fontWeight: 600, mb: 1 }}>
          Pilot Ressurser
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/pilot')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ color: '#3598b7', fontWeight: 600 }}>
            Planlegg Oppdrag
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            backgroundColor: '#ffffff', 
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="h6" sx={{ color: '#3598b7', fontWeight: 600 }}>
                Obligatoriske Skjemaer
              </Typography>
            </Box>
            <List disablePadding sx={{ flex: 1 }}>
              {requiredLinks.map((form, index) => (
                <React.Fragment key={index}>
                  <ResourceLink {...form} />
                  {index < requiredLinks.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            backgroundColor: '#ffffff', 
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="h6" sx={{ color: '#3598b7', fontWeight: 600 }}>
                Områdesjekk
              </Typography>
            </Box>
            <List disablePadding sx={{ flex: 1 }}>
              {areaChecks.map((check, index) => (
                <React.Fragment key={index}>
                  <ResourceLink {...check} />
                  {index < areaChecks.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ 
            backgroundColor: '#ffffff', 
            borderRadius: 0,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="h6" sx={{ color: '#3598b7', fontWeight: 600 }}>
                Sjekkliste
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, color: 'rgba(0, 0, 0, 0.7)' }}>
                Følgende punkter må gjennomgås:
              </Typography>
              {Object.entries(checklistItems).map(([section, items]) => (
                <Box key={section} sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#3598b7', 
                      fontWeight: 600,
                      mb: 1
                    }}
                  >
                    {section}
                  </Typography>
                  <List disablePadding>
                    {items.map((item, index) => (
                      <InfoItem key={index} text={item} />
                    ))}
                  </List>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanMission; 